import React, { useEffect, useState, useMemo } from 'react';
import { Loader2, Save, CalendarClock, Mail, Play, AlertTriangle, CheckCircle2, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { adminApi } from './adminApi';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { fmtServerDateTime } from './utils/datetime';

const TIMEZONES = [
  { value: 'Asia/Kolkata', label: 'Asia/Kolkata (IST)' },
  { value: 'UTC', label: 'UTC' },
  { value: 'America/New_York', label: 'America/New_York (ET)' },
  { value: 'America/Los_Angeles', label: 'America/Los_Angeles (PT)' },
  { value: 'Europe/London', label: 'Europe/London (GMT/BST)' },
  { value: 'Asia/Dubai', label: 'Asia/Dubai (GST)' },
  { value: 'Asia/Singapore', label: 'Asia/Singapore (SGT)' },
  { value: 'Australia/Sydney', label: 'Australia/Sydney (AET)' },
];

/**
 * Schedule card for the SuperAdmin Diagnostics page.
 *
 * Persists to `db.diagnostic_schedules` via PUT /api/admin/diagnostics/schedule.
 * The in-process scheduler picks up changes within 60 seconds (no restart
 * needed). "Run now" forces an immediate execution to verify the email
 * pipeline without waiting for tomorrow's window.
 */
export function ScheduleCard({ suites, onRunFinished }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [config, setConfig] = useState({
    enabled: false,
    time_local: '03:00',
    timezone: 'Asia/Kolkata',
    suite_keys: ['all_smoke'],
    email_recipients: [],
    email_on_failure_only: true,
    last_run_at: null,
    last_status: null,
    last_error: null,
  });
  const [emailInput, setEmailInput] = useState('');

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const r = await adminApi.get('/admin/diagnostics/schedule');
        if (cancel) return;
        setConfig(r.data);
        setEmailInput((r.data.email_recipients || []).join(', '));
      } catch (e) {
        if (!cancel) toast.error(e?.response?.data?.detail || 'Failed to load schedule');
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, []);

  // Suites grouped by category for the multi-select.
  const groupedSuites = useMemo(() => {
    const map = {};
    (suites || []).forEach((s) => {
      const c = s.category || 'Misc';
      (map[c] = map[c] || []).push(s);
    });
    return map;
  }, [suites]);

  const toggleSuite = (key) => {
    setConfig((c) => ({
      ...c,
      suite_keys: c.suite_keys.includes(key)
        ? c.suite_keys.filter((k) => k !== key)
        : [...c.suite_keys, key],
    }));
  };

  const save = async () => {
    setSaving(true);
    try {
      // Parse comma/space-separated email list.
      const recipients = emailInput.split(/[,;\s]+/).map((s) => s.trim()).filter(Boolean);
      const payload = { ...config, email_recipients: recipients };
      const r = await adminApi.put('/admin/diagnostics/schedule', payload);
      setConfig(r.data);
      setEmailInput((r.data.email_recipients || []).join(', '));
      toast.success('Schedule saved.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const runNow = async () => {
    setRunning(true);
    try {
      const r = await adminApi.post('/admin/diagnostics/schedule/run-now');
      setConfig(r.data);
      const ok = r.data.last_status === 'pass';
      if (ok) toast.success('Run completed — all green.');
      else toast.error('Run completed with failures. Check the dashboard.');
      onRunFinished?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Run failed');
    } finally {
      setRunning(false);
    }
  };

  if (loading) {
    return (
      <section className="rounded-xl border border-slate-800 bg-slate-900/50 p-6 text-slate-400 flex items-center gap-2">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading schedule…
      </section>
    );
  }

  return (
    <section
      className="rounded-xl border border-slate-800 bg-slate-900/50 p-6 space-y-5"
      data-testid="admin-diag-schedule"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-base font-semibold flex items-center gap-2">
            <CalendarClock className="w-4 h-4 text-amber-400" /> Scheduled run
          </h2>
          <p className="text-xs text-slate-400 mt-1">
            Cron-style daily run of the smoke bundle. Fires once per day at the
            configured time. Sends an email digest to the recipients below
            {config.email_on_failure_only ? ' only when something fails' : ' after every run'}.
          </p>
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-200 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={config.enabled}
            onChange={(e) => setConfig({ ...config, enabled: e.target.checked })}
            data-testid="admin-diag-schedule-enabled"
            className="w-4 h-4 accent-amber-500"
          />
          {config.enabled ? 'Enabled' : 'Disabled'}
        </label>
      </div>

      {/* Last run status pill */}
      {config.last_run_at && (
        <LastRunPill at={config.last_run_at} status={config.last_status} error={config.last_error} />
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <Label className="text-slate-300 text-xs uppercase tracking-wider">Time (24h)</Label>
          <Input
            type="time"
            value={config.time_local}
            onChange={(e) => setConfig({ ...config, time_local: e.target.value })}
            className="mt-1 bg-slate-800 border-slate-700 font-mono"
            data-testid="admin-diag-schedule-time"
          />
        </div>
        <div className="md:col-span-2">
          <Label className="text-slate-300 text-xs uppercase tracking-wider">Timezone</Label>
          <select
            value={config.timezone}
            onChange={(e) => setConfig({ ...config, timezone: e.target.value })}
            className="mt-1 w-full bg-slate-800 border border-slate-700 rounded-md px-3 py-2 text-sm"
            data-testid="admin-diag-schedule-tz"
          >
            {TIMEZONES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>
      </div>

      <div>
        <Label className="text-slate-300 text-xs uppercase tracking-wider flex items-center gap-2">
          <Mail className="w-3.5 h-3.5" /> Email recipients
        </Label>
        <Input
          value={emailInput}
          onChange={(e) => setEmailInput(e.target.value)}
          placeholder="admin@example.com, ops@example.com"
          className="mt-1 bg-slate-800 border-slate-700"
          data-testid="admin-diag-schedule-emails"
        />
        <label className="flex items-center gap-2 text-xs text-slate-400 mt-2 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={config.email_on_failure_only}
            onChange={(e) => setConfig({ ...config, email_on_failure_only: e.target.checked })}
            data-testid="admin-diag-schedule-failonly"
            className="w-3.5 h-3.5 accent-amber-500"
          />
          Email only when at least one test fails (recommended — prevents inbox noise)
        </label>
      </div>

      <div>
        <Label className="text-slate-300 text-xs uppercase tracking-wider">Suites to run</Label>
        <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-1.5 max-h-64 overflow-y-auto pr-2">
          {Object.entries(groupedSuites).map(([cat, list]) => (
            <div key={cat} className="space-y-1">
              <p className="text-[10px] uppercase tracking-wider text-amber-400/80 font-semibold">{cat}</p>
              {list.map((s) => (
                <label
                  key={s.key}
                  className="flex items-start gap-2 text-xs text-slate-300 cursor-pointer hover:bg-slate-800/50 rounded px-1.5 py-1"
                >
                  <input
                    type="checkbox"
                    checked={config.suite_keys.includes(s.key)}
                    onChange={() => toggleSuite(s.key)}
                    data-testid={`admin-diag-schedule-suite-${s.key}`}
                    className="mt-0.5 w-3.5 h-3.5 accent-amber-500 shrink-0"
                  />
                  <span className="leading-tight break-words">{s.title}</span>
                </label>
              ))}
            </div>
          ))}
        </div>
        <p className="text-[10px] text-slate-500 mt-2">
          {config.suite_keys.length} suite{config.suite_keys.length === 1 ? '' : 's'} selected.
          Tip: stick with <code className="bg-slate-800 px-1 rounded">all_smoke</code> for sub-30s daily runs.
        </p>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2 pt-2 border-t border-slate-800">
        <Button
          onClick={runNow}
          disabled={running || saving || config.suite_keys.length === 0}
          variant="outline"
          className="border-slate-700 bg-slate-800 hover:bg-slate-700 text-slate-200"
          data-testid="admin-diag-schedule-runnow"
        >
          {running ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Play className="w-4 h-4 mr-1.5" />}
          Run now (test pipeline)
        </Button>
        <Button
          onClick={save}
          disabled={saving}
          className="bg-amber-500 hover:bg-amber-600 text-slate-950"
          data-testid="admin-diag-schedule-save"
        >
          {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
          Save schedule
        </Button>
      </div>
    </section>
  );
}

function LastRunPill({ at, status, error }) {
  const map = {
    pass: { Icon: CheckCircle2, cls: 'border-emerald-800 bg-emerald-950/30 text-emerald-300' },
    fail: { Icon: XCircle, cls: 'border-rose-800 bg-rose-950/30 text-rose-300' },
    warn: { Icon: AlertTriangle, cls: 'border-amber-800 bg-amber-950/30 text-amber-300' },
  };
  const { Icon, cls } = map[status] || map.warn;
  return (
    <div
      className={`rounded-lg border ${cls} px-3 py-2 text-xs flex items-start gap-2`}
      data-testid="admin-diag-schedule-laststatus"
    >
      <Icon className="w-4 h-4 shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <p>Last scheduled run {fmtServerDateTime(at)} — <strong>{(status || 'warn').toUpperCase()}</strong></p>
        {error && <p className="mt-1 text-[11px] opacity-80 break-words">{error}</p>}
      </div>
    </div>
  );
}

export default ScheduleCard;
